import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { db, SYNC_STATUS, addToSyncQueue, recordHistoryBatch } from '../db/shiksha-db';
import { StatusBar } from '../components/StatusBar';
import { ArrowLeft, Check, X, ShieldAlert, CloudUpload } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

/**
 * Attendance register — date picker + student list with Present/Absent toggles.
 * All writes go to Dexie → syncQueue. 48px touch targets per DESIGN.md.
 */
export function AttendancePage() {
  const { user } = useAuth();
  
  // Generate a batchId once per page session
  const [batchId] = useState(() => crypto.randomUUID());
  // Record this batch in history if it gets used
  const [historyRecorded, setHistoryRecorded] = useState(false);

  const teacherProfile = useLiveQuery(
    () => (user?.email ? db.teachers.where('email').equals(user.email).first() : null),
    [user?.email]
  );
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0],
  );

  const students = useLiveQuery(async () => {
    const all = await db.students.orderBy('rollNo').toArray();
    
    if (user?.role === 'admin' || teacherProfile?.role === 'admin') {
      return all;
    }
    
    if (teacherProfile?.isClassTeacher && teacherProfile?.classTeacherOf) {
      const cohortStr = teacherProfile.classTeacherOf.toUpperCase();
      const sectionStr = (teacherProfile.classTeacherSection || '').toUpperCase().trim();
      
      const baseClass = cohortStr.split('-')[0].trim();
      
      return all.filter(s => {
        const sClass = (s.class || '').toUpperCase();
        const sSection = (s.section || '').toUpperCase().trim();
        
        let isMatch = false;
        if (baseClass === 'CLASS VI') isMatch = ['6','VI','CLASS VI'].includes(sClass);
        else if (baseClass === 'CLASS VII') isMatch = ['7','VII','CLASS VII'].includes(sClass);
        else if (baseClass === 'CLASS VIII') isMatch = ['8','VIII','CLASS VIII'].includes(sClass);
        else if (baseClass === 'CLASS IX') isMatch = ['9','IX','CLASS IX'].includes(sClass);
        else if (baseClass === 'CLASS X') isMatch = ['10','X','CLASS X'].includes(sClass);
        else isMatch = sClass === baseClass;
        
        if (!isMatch) return false;
        
        if (!sectionStr) return true; // If teacher has no section, they see all sections of the class
        if (!sSection) return true; // If student has no section, they are visible in all sections (cross-pollination)
        
        return sSection === sectionStr;
      });
    }
    
    return []; // Access denied or no class assigned
  }, [user, teacherProfile]);
  const attendance = useLiveQuery(
    () => db.attendance.where('date').equals(selectedDate).toArray(),
    [selectedDate],
  );

  const getStatus = (studentId) => {
    return attendance?.find((a) => a.studentId === studentId)?.status || null;
  };

  const getSyncStatus = (studentId) => {
    return attendance?.find((a) => a.studentId === studentId)?.syncStatus || null;
  };

  const markAttendance = async (studentId, status) => {
    const existing = await db.attendance
      .where({ studentId, date: selectedDate })
      .first();

    let recordId;
    if (existing) {
      await db.attendance.update(existing.id, {
        status,
        syncStatus: SYNC_STATUS.QUEUED,
      });
      recordId = existing.id;
    } else {
      recordId = await db.attendance.add({
        studentId,
        date: selectedDate,
        status,
        syncStatus: SYNC_STATUS.QUEUED,
        batchId,
      });
    }

    await addToSyncQueue('attendance', recordId, existing ? 'update' : 'create', {
      studentId,
      date: selectedDate,
      status,
    }, batchId);

    if (!historyRecorded) {
      recordHistoryBatch(batchId, `Saved Attendance for ${selectedDate}`);
      setHistoryRecorded(true);
    }
  };

  const presentCount = attendance?.filter((a) => a.status === 'present').length ?? 0;
  const absentCount  = attendance?.filter((a) => a.status === 'absent').length ?? 0;

  if (user?.role !== 'admin' && teacherProfile?.role !== 'admin' && !teacherProfile?.isClassTeacher) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center p-4">
        <div className="bg-white border border-red-100 rounded-xl p-8 max-w-md w-full text-center shadow-sm">
          <ShieldAlert size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-ink mb-2">Access Denied</h2>
          <p className="text-graphite mb-6">
            You are not assigned as a Class Teacher. Only designated class teachers can mark attendance.
          </p>
          <Link to="/teacher/dashboard" className="text-indigo-600 font-medium hover:underline">
            Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper pb-8">
      <div className="max-w-4xl mx-auto px-4 py-4">
        {/* ── Header ── */}
        <div className="flex items-center gap-4 mb-6">
          <Link
            to="/teacher/dashboard"
            className="p-2 rounded-lg bg-white border border-divider hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={20} className="text-ink" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-ink">Attendance Register</h1>
            {teacherProfile?.isClassTeacher && (
              <p className="text-sm text-graphite font-secondary mt-0.5">
                {teacherProfile.classTeacherOf} {teacherProfile.classTeacherSection ? `- ${teacherProfile.classTeacherSection}` : ''}
              </p>
            )}
            <p className="text-sm text-graphite font-secondary">
              {presentCount} present · {absentCount} absent
            </p>
          </div>
        </div>

        {/* ── Date Picker ── */}
        <div className="mb-6">
          <label htmlFor="attendance-date" className="block text-sm font-medium text-ink mb-1">
            Select Date
          </label>
          <input
            type="date"
            id="attendance-date"
            value={selectedDate}
            max={teacherProfile?.role !== 'admin' && user?.role !== 'admin' ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] : undefined}
            min={teacherProfile?.role !== 'admin' && user?.role !== 'admin' ? new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] : undefined}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-full sm:w-auto px-4 py-3 bg-white border border-gray-300 rounded-lg text-ink focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-shadow"
          />
        </div>


        {/* ── Student List ── */}
        <div className="space-y-2">
          {students?.map((student) => {
            const status = getStatus(student.id);
            const syncStatus = getSyncStatus(student.id);
            return (
              <div
                key={student.id}
                className="flex items-center justify-between bg-white border border-divider rounded-xl px-4 py-3"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-base font-medium text-ink">{student.name}</p>
                    {syncStatus === SYNC_STATUS.QUEUED && (
                      <CloudUpload size={16} className="text-amber-500" title="Pending Sync" />
                    )}
                  </div>
                  <p className="text-sm text-graphite font-secondary">Roll #{student.rollNo}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => markAttendance(student.id, 'present')}
                    aria-label={`Mark ${student.name} present`}
                    className={`min-w-[48px] min-h-[48px] flex items-center justify-center rounded-lg border-2 font-semibold text-sm transition-all duration-200 ${
                      status === 'present'
                        ? 'bg-green-100 border-synced text-green-800'
                        : 'bg-white border-gray-300 text-gray-500 hover:border-synced hover:text-green-700'
                    }`}
                  >
                    <Check size={20} />
                  </button>
                  <button
                    onClick={() => markAttendance(student.id, 'absent')}
                    aria-label={`Mark ${student.name} absent`}
                    className={`min-w-[48px] min-h-[48px] flex items-center justify-center rounded-lg border-2 font-semibold text-sm transition-all duration-200 ${
                      status === 'absent'
                        ? 'bg-red-100 border-conflict text-red-800'
                        : 'bg-white border-gray-300 text-gray-500 hover:border-conflict hover:text-red-700'
                    }`}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            );
          })}

          {(!students || students.length === 0) && (
            <div className="text-center py-12 text-graphite">
              <p className="text-base">No students found.</p>
              <p className="text-sm mt-1">
                Go to the{' '}
                <Link to="/dashboard" className="text-action underline">
                  Dashboard
                </Link>{' '}
                to seed demo data.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
